"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

const modalities = [
  {
    id: "pilates",
    title: "Pilates",
    subtitle: "Método Clássico",
    number: "01",
    description: "Fortalecimento profundo, postura correta e mobilidade ampliada. O método original que transforma corpo e mente.",
    benefits: ["Postura", "Mobilidade", "Fortalecimento"],
    image: "https://images.unsplash.com/photo-1717500252179-2811af29e4f7",
    alt: "Aluna praticando Pilates no reformer com instrutora em estúdio iluminado"
  },
  {
    id: "pilates-funcional",
    title: "Pilates Funcional",
    subtitle: "Movimento Integrado",
    number: "02",
    description: "Integração do Pilates com movimentos funcionais para ganho de força, equilíbrio e prevenção de lesões.",
    benefits: ["Força", "Equilíbrio", "Prevenção"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12619587c-1772741698050.png",
    alt: "Aula de Pilates funcional com exercícios de equilíbrio e força"
  },
  {
    id: "circuito-funcional",
    title: "Circuito Funcional",
    subtitle: "Alta Performance",
    number: "03",
    description: "Circuito dinâmico que combina resistência, agilidade e condicionamento físico de forma segura e eficiente.",
    benefits: ["Condicionamento", "Agilidade", "Resistência"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ffbc531-1772741695699.png",
    alt: "Circuito funcional com exercícios de resistência e agilidade em estúdio"
  }
];

export default function ModalidadesPreview() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.08 }
    );
    ref?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 px-6 md:px-10 bg-cream" ref={ref}>
      <div className="max-w-8xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
          <div>
            <span className="reveal-hidden subheading mb-5 block">Modalidades</span>
            <h2 className="reveal-hidden reveal-delay-1 font-serif text-forest leading-tight"
              style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
              Práticas que{" "}
              <span className="italic font-light text-sage">transformam</span>
            </h2>
          </div>
          <Link href="/services" className="reveal-hidden reveal-delay-2 btn-secondary self-start md:self-auto">
            Ver todas as modalidades
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {modalities?.map((mod, i) => (
            <div
              key={mod?.id}
              className={`service-card reveal-hidden reveal-delay-${i + 1} rounded-3xl overflow-hidden bg-white`}
              style={{ boxShadow: '0 4px 24px rgba(26,61,26,0.06), 0 12px 48px rgba(26,61,26,0.04)', border: '1px solid rgba(232,226,214,0.5)' }}
            >
              {/* Image */}
              <div className="h-60 overflow-hidden relative">
                <AppImage
                  src={mod?.image}
                  alt={mod?.alt}
                  width={600}
                  height={400}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                {/* Decorative number overlay */}
                <div className="absolute top-4 right-5 font-serif font-light leading-none select-none"
                  style={{ fontSize: '5rem', color: 'rgba(255,255,255,0.15)' }}>
                  {mod?.number}
                </div>
              </div>
              {/* Content */}
              <div className="p-8 relative">
                {/* Gold number behind title */}
                <div className="absolute top-4 right-6 font-serif font-light leading-none select-none"
                  style={{ fontSize: '5rem', color: 'rgba(201,168,76,0.12)' }}>
                  {mod?.number}
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--gold)' }}>{mod?.subtitle}</span>
                <h3 className="font-serif mt-2 mb-4" style={{ fontSize: '1.6rem', color: 'var(--forest)' }}>{mod?.title}</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--stone)' }}>{mod?.description}</p>
                {/* Benefit chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {mod?.benefits?.map((b) => (
                    <span key={b} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: 'var(--sage-pale)', color: 'var(--forest-mid)' }}>
                      {b}
                    </span>
                  ))}
                </div>
                <a
                  href={`https://wa.me/5551999999999?text=Olá! Tenho interesse em ${mod?.title}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold flex items-center gap-2 transition-colors"
                  style={{ color: 'var(--forest)' }}
                >
                  Saber mais
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
